import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useState } from 'react';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { loadSingleBatch, setBatch } from '@reducers/batch';
import { useSelector } from 'react-redux';
import { selectSelectedItem } from '@reducers/machines';
import { Machine, MachineStatisticOEETeep } from '@interfaces/machines';
import { machineStatisticsOeeTeep } from '@reducers/statistics';
import { getTools } from '@reducers/clampingTools';
import { withAbortableErrorHandler, localTime, formatStartOf, ncFileStatuses, ALL_MACHINES } from '@helpers';
import { DATE_FORMAT } from '@v2/pages/common/constants/base.constants';
import { TimePeriod } from '@v2/pages/machines/common/enums';
import { getNCFileById } from '@reducers/manufacture';
import { NCFileFirstRanInfoType } from '@v2/pages/machines/modules/now/components/Overview/types';

export const useMachinesNowOverviewViewModel = () => {
  const dispatch = useAppDispatch();

  const selectedMachine: Machine = useSelector(selectSelectedItem);

  const batchId = selectedMachine?.machineStatus?.productionBatchId;
  const machineId = selectedMachine?.id;
  const ncFileId = selectedMachine?.machineStatus?.ncFileId;
  const ncFileStatus = selectedMachine?.machineStatus?.ncFileStatus;

  const shouldLoadNCFile = [ncFileStatuses.Change, ncFileStatuses.Ignored, ncFileStatuses.Setup].includes(ncFileStatus);

  const [toolsLength, setToolsLength] = useState<number>(0);
  const [oeeTeepData, setOeeTeepData] = useState<MachineStatisticOEETeep | null>(null);
  const [ncFileFirstRanInfo, setNCFileFirstRanInfo] = useState<NCFileFirstRanInfoType | null>(null);

  useFocusEffect(
    useCallback(() => {
      const abortControllerBatch = new AbortController();
      const abortControllerStatistics = new AbortController();
      const abortControllerTools = new AbortController();
      const abortControllerNCFile = new AbortController();

      // No single-machine data to fetch when "All Machines" is selected
      const fetchSelectedMachineData = async () => {
        if (String(machineId) === ALL_MACHINES) return;

        const promises: Promise<void>[] = [];

        if (batchId != null) {
          promises.push(
            withAbortableErrorHandler({
              promise: dispatch(loadSingleBatch(batchId, abortControllerBatch.signal)),
              signal: abortControllerBatch.signal,
              errorMessage: 'Error loading batch',
              onSuccess: undefined,
            })
          );
        }

        if (machineId != null) {
          promises.push(
            withAbortableErrorHandler({
              promise: dispatch(
                machineStatisticsOeeTeep(
                  { machineId, date: localTime().startOf(formatStartOf(TimePeriod.DAY)).format(DATE_FORMAT), period: TimePeriod.DAY },
                  abortControllerStatistics.signal
                )
              ),
              signal: abortControllerStatistics.signal,
              errorMessage: 'Error loading OEE/TEEP statistics',
              onSuccess: (data) => {
                const machineOee = data?.machineStatistics?.find((s) => s.machineId === machineId);
                setOeeTeepData(machineOee ?? null);
              },
            })
          );
        }

        if (ncFileId != null) {
          promises.push(
            withAbortableErrorHandler({
              promise: dispatch(getTools({ ncFileId })),
              signal: abortControllerTools.signal,
              errorMessage: 'Error loading tools',
              onSuccess: (tools) => {
                if (tools) {
                  setToolsLength(tools.length);
                }
              },
            })
          );
        }

        if (shouldLoadNCFile && ncFileId != null) {
          promises.push(
            withAbortableErrorHandler({
              promise: dispatch(getNCFileById(ncFileId, ['FirstRanOn', 'FirstRanByMachineName'])),
              signal: abortControllerNCFile.signal,
              errorMessage: 'Error loading NC file info',
              onSuccess: (response) => {
                if (response) {
                  setNCFileFirstRanInfo({ firstRanOn: response?.FirstRanOn, firstRanByMachineName: response?.FirstRanByMachineName });
                }
              },
            })
          );
        }

        await Promise.allSettled(promises);
      };

      fetchSelectedMachineData();

      return () => {
        abortControllerBatch.abort();
        abortControllerStatistics.abort();
        abortControllerTools.abort();
        abortControllerNCFile.abort();
        
        setToolsLength(0);
        setOeeTeepData(null);
        setNCFileFirstRanInfo(null);
        dispatch(setBatch(null));
      };
    }, [batchId, machineId, ncFileId, shouldLoadNCFile, dispatch])
  );

  return {
    toolsLength,
    oeeTeepData,
    ncFileFirstRanInfo,
  };
};
